import { clearObject } from "@/modules/utils";
import { useApiWithAuth } from "@/modules/api";
import { PermissionPayload } from "@/modules/auth";

interface OrcamentoFilterPayload {
  idCliente?: number | null;
  idTransportador?: number | null;
  estagio?: string | null;
  origem?: string | null;
  /** Primeiro dia do mês selecionado (filtra por dataCadastro). */
  dataMesReferencia?: Date | string | null;
}

interface OrcamentoItemPayload {
  idOrcamentoItem?: number | null;
  idOrcamento?: number | null;
  idLicenca?: number | null;
  placa?: string | null;
  idOrgao?: number | null;
  tipoSolicitacao?: number | null;
  taxaOrgao?: number | null;
  honorario?: number | null;
  valorUnitario?: number | null;
  revisaoNecessaria?: boolean | null;
  fontePrecificacao?: string | null;
  nomeOrgao?: string | null;
  siglaOrgao?: string | null;
  permissionInfo?: PermissionPayload;
}

interface OrcamentoPayload {
  idOrcamento?: number | null;
  idTransportador?: number | null;
  estagio?: string | null;
  valorTotal?: number | null;
  dataEnvio?: Date | null;
  origem?: string | null;
  observacoes?: string | null;
  itens: Array<OrcamentoItemPayload>;
  dataCadastro?: Date | null;
  dataAlteracao?: Date | null;
  nomeTransportador?: string | null;
  cpfCnpjTransportador?: string | null;
  qtdItens?: number | null;
  idArtEngenheiroTransportador?: number | null;
  nomeArtSelecionada?: string | null;
  qtdLicencasComErro?: number | null;
  permissionInfo?: PermissionPayload;
}

interface ConfirmarUploadArtPayload {
  keyBlDocumentoTemp: string;
  art: string;
  dataVencimento: Date | string;
  idEngenheiro?: number | null;
}

interface ConfirmarUploadAetDnitPayload {
  idLicencaMae: number;
  keyBlDocumentoTemp: string;
  numeroAet: string;
  dtInicial: Date | string;
  dtFinal: Date | string;
}

interface PendenciaAetDnitPayload {
  idLicencaMae: number;
  idVeiculoTrator: number;
  placaCavalo?: string | null;
  idItem: number;
}

interface PendenciasRenovacaoPayload {
  precisaArt: boolean;
  pendenciasAetDnit: Array<PendenciaAetDnitPayload>;
}

interface RenovacaoLicencaPreviewPayload {
  idLicencaMae: number;
  idOrgao?: number | null;
  siglaOrgao?: string | null;
  numeroAetOrigem?: string | null;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  licenca: any;
  /* eslint-enable @typescript-eslint/no-explicit-any */
}

interface StatusRenovacaoLicencaPayload {
  idLicenca: number;
  idLicencaOrgao: number;
  idOrgao: number;
  siglaOrgao: string;
  placa: string;
  statusRobo?: string | null;
  flSituacaoOrgao?: string | null;
  situacao?: string | null;
  msgCliente?: string | null;
  logErro?: string | null;
  clienteErro?: boolean | null;
  keyImgErro?: string | null;
  temErro?: boolean | null;
}

interface EstagioUpdatePayload {
  estagio: string;
  observacoes?: string | null;
}

interface MergeOrcamentoPayload {
  idOrcamentoOrigem: number;
  idOrcamentoDestino: number;
}

interface UpdateItemPrecoPayload {
  honorario: number;
}

interface WhatsAppPayloadResponse {
  url: string;
  mensagem: string;
  pdfUrl: string;
}

interface EstagioOrcamento {
  codigo: string;
  nome: string;
  cor: string;
  arquivado: boolean;
}

const ESTAGIOS: Array<EstagioOrcamento> = [
  // Raias de mensagem
  { codigo: "NOV", nome: "Novo", cor: "#90A4AE", arquivado: false },
  { codigo: "ORC", nome: "Orçando", cor: "#42A5F5", arquivado: false },
  { codigo: "ENV", nome: "Enviado", cor: "#FFA726", arquivado: false },
  { codigo: "ACE", nome: "Aceito", cor: "#66BB6A", arquivado: false },
  // Raias operacionais
  {
    codigo: "SAR",
    nome: "Solicitar Documentos",
    cor: "#FFB300",
    arquivado: false
  },
  { codigo: "SAE", nome: "Solicitar AET", cor: "#5C6BC0", arquivado: false },
  { codigo: "AES", nome: "AET Solicitada", cor: "#00897B", arquivado: false },
  { codigo: "EMI", nome: "AET Emitida", cor: "#26A69A", arquivado: false },
  // Arquivados
  {
    codigo: "EMM",
    nome: "Emitido Manualmente",
    cor: "#7E57C2",
    arquivado: true
  },
  { codigo: "REC", nome: "Recusado", cor: "#EF5350", arquivado: true },
  { codigo: "PAU", nome: "Pausado", cor: "#8D6E63", arquivado: true }
];

const TRANSICOES: Record<string, Array<string>> = {
  NOV: ["ORC", "ENV"],
  ORC: ["ENV"],
  ENV: ["ACE", "REC", "PAU"],
  ACE: ["SAR", "SAE", "EMM"],
  SAR: ["SAE"],
  SAE: ["AES"],
  AES: ["EMI"],
  EMI: [],
  EMM: [],
  REC: [],
  PAU: ["ORC"]
};

// Transições disponíveis via drag-and-drop no Kanban.
// Etapas operacionais (ACE→SAR/SAE, SAR→SAE, SAE→AES) exigem o fluxo
// dentro do card (botão Renovar / upload de ART / aceitar etapa) — D&D
// bloqueia e instrui o usuário a abrir o card. ACE→EMM também passa
// pelo botão "Emitir manualmente" pra ter confirmação explícita.
const TRANSICOES_DRAG: Record<string, Array<string>> = {
  NOV: ["ORC", "ENV"],
  ORC: ["ENV"],
  ENV: ["ACE", "REC", "PAU"],
  ACE: [],
  SAR: [],
  SAE: [],
  AES: [],
  EMI: [],
  EMM: [],
  REC: [],
  PAU: ["ORC"]
};

const ORIENTACOES_FLUXO: Record<string, string> = {
  ACE:
    'Abra o card e use "Renovar" para iniciar o pipeline, ou "Emitir manualmente" para arquivar como já emitido fora do sistema.',
  SAR:
    "Abra o card e anexe os documentos pendentes (ART e/ou AET DNIT do cavalo) para prosseguir.",
  SAE: 'Abra o card e clique em "Aceitar etapa e gerar AETs".',
  AES:
    "Aguarde os robôs concluírem ou abra o card para reenviar licenças com erro."
};

const ORIGENS: Array<{ codigo: string; nome: string }> = [
  { codigo: "MAN", nome: "Criação manual" },
  { codigo: "VAR", nome: "Varredura de renovação" },
  { codigo: "ONB", nome: "Onboarding de transportador" }
];

const useOrcamentoService = () => {
  const {
    data,
    loading,
    get,
    post,
    patch,
    del,
    downloadFileNewTab
  } = useApiWithAuth();

  const searchOrcamentos = (
    filter: OrcamentoFilterPayload,
    page: number,
    skipEmptyToast = false
  ): Promise<void> => {
    return get(
      "/orcamento",
      {
        ...clearObject(filter),
        ...{
          page: page
        }
      },
      skipEmptyToast
        ? ({ skipEmptyToast: true } as Record<string, unknown>)
        : undefined
    );
  };

  const getOrcamento = (idOrcamento: number): Promise<void> => {
    return get(`/orcamento/${idOrcamento}`);
  };

  const saveNewOrcamento = (orcamento: OrcamentoPayload): Promise<void> => {
    return post("/orcamento", orcamento);
  };

  const updateEstagio = (
    idOrcamento: number,
    payload: EstagioUpdatePayload
  ): Promise<void> => {
    return patch(`/orcamento/${idOrcamento}/estagio`, payload);
  };

  const mesclarOrcamentos = (payload: MergeOrcamentoPayload): Promise<void> => {
    return post("/orcamento/mesclar", payload);
  };

  const adicionarItem = (
    idOrcamento: number,
    item: OrcamentoItemPayload
  ): Promise<void> => {
    return post(`/orcamento/${idOrcamento}/itens`, item);
  };

  const removerItem = (
    idOrcamento: number,
    idOrcamentoItem: number
  ): Promise<void> => {
    return del(`/orcamento/${idOrcamento}/itens/${idOrcamentoItem}`);
  };

  const atualizarPrecoItem = (
    idOrcamento: number,
    idOrcamentoItem: number,
    payload: UpdateItemPrecoPayload
  ): Promise<void> => {
    return patch(
      `/orcamento/${idOrcamento}/itens/${idOrcamentoItem}/preco`,
      payload
    );
  };

  const getWhatsAppPayload = (idOrcamento: number): Promise<void> => {
    return get(`/orcamento/${idOrcamento}/whatsapp`);
  };

  const iniciarRenovacao = (idOrcamento: number): Promise<void> => {
    return post(`/orcamento/${idOrcamento}/renovar`);
  };

  const uploadArtRenovacao = (
    idOrcamento: number,
    payload: ConfirmarUploadArtPayload
  ): Promise<void> => {
    return post(`/orcamento/${idOrcamento}/art-upload`, payload);
  };

  const uploadAetDnitRenovacao = (
    idOrcamento: number,
    payload: ConfirmarUploadAetDnitPayload
  ): Promise<void> => {
    return post(`/orcamento/${idOrcamento}/dnit-upload`, payload);
  };

  const getPendenciasRenovacao = (idOrcamento: number): Promise<void> => {
    return get(`/orcamento/${idOrcamento}/pendencias-renovacao`);
  };

  const getPreviewLicencas = (idOrcamento: number): Promise<void> => {
    return get(`/orcamento/${idOrcamento}/preview-licencas`);
  };

  const aceitarEtapaRenovacao = (
    idOrcamento: number,
    previews: Array<RenovacaoLicencaPreviewPayload>
  ): Promise<void> => {
    return post(`/orcamento/${idOrcamento}/aceitar-etapa`, previews);
  };

  const getStatusLicencas = (idOrcamento: number): Promise<void> => {
    return get(`/orcamento/${idOrcamento}/status-licencas`);
  };

  const reenviarLicencaOrgao = (
    idOrcamento: number,
    idLicencaOrgao: number
  ): Promise<void> => {
    return patch(
      `/orcamento/${idOrcamento}/licenca-orgao/${idLicencaOrgao}/reenviar`
    );
  };

  const downloadPdf = (idOrcamento: number): Promise<void> => {
    return downloadFileNewTab(`/orcamento/${idOrcamento}/pdf`);
  };

  const getEstagios = (incluirArquivados = false): Array<EstagioOrcamento> => {
    return incluirArquivados ? ESTAGIOS : ESTAGIOS.filter((e) => !e.arquivado);
  };

  const getEstagio = (codigo?: string | null): EstagioOrcamento | undefined => {
    if (!codigo) {
      return undefined;
    }
    return ESTAGIOS.find((e) => e.codigo === codigo);
  };

  const getTransicoesPermitidas = (
    estagioAtual?: string | null
  ): Array<string> => {
    if (!estagioAtual) {
      return [];
    }
    return TRANSICOES[estagioAtual] ?? [];
  };

  const isTransicaoPermitida = (
    de?: string | null,
    para?: string | null
  ): boolean => {
    if (!de || !para) {
      return false;
    }
    return getTransicoesPermitidas(de).includes(para);
  };

  const isTransicaoArrastavel = (
    de?: string | null,
    para?: string | null
  ): boolean => {
    if (!de || !para) {
      return false;
    }
    return (TRANSICOES_DRAG[de] ?? []).includes(para);
  };

  const getOrientacaoFluxo = (estagio?: string | null): string | undefined => {
    if (!estagio) {
      return undefined;
    }
    return ORIENTACOES_FLUXO[estagio];
  };

  const getOrigens = () => ORIGENS;

  const safeOrcamento = (
    entity?: OrcamentoPayload | null
  ): OrcamentoPayload => {
    const ret = entity ?? {
      itens: []
    };
    ret.itens = ret.itens ?? [];
    return ret;
  };

  const safeFilterOrcamento = (): OrcamentoFilterPayload => {
    return {};
  };

  return {
    data,
    loading,
    searchOrcamentos,
    getOrcamento,
    saveNewOrcamento,
    updateEstagio,
    mesclarOrcamentos,
    adicionarItem,
    removerItem,
    atualizarPrecoItem,
    getWhatsAppPayload,
    downloadPdf,
    iniciarRenovacao,
    uploadArtRenovacao,
    uploadAetDnitRenovacao,
    getPendenciasRenovacao,
    getPreviewLicencas,
    aceitarEtapaRenovacao,
    getStatusLicencas,
    reenviarLicencaOrgao,
    getEstagios,
    getEstagio,
    getTransicoesPermitidas,
    isTransicaoPermitida,
    isTransicaoArrastavel,
    getOrientacaoFluxo,
    getOrigens,
    safeOrcamento,
    safeFilterOrcamento
  };
};

export {
  useOrcamentoService,
  OrcamentoPayload,
  OrcamentoItemPayload,
  OrcamentoFilterPayload,
  EstagioUpdatePayload,
  MergeOrcamentoPayload,
  UpdateItemPrecoPayload,
  WhatsAppPayloadResponse,
  EstagioOrcamento,
  ConfirmarUploadArtPayload,
  ConfirmarUploadAetDnitPayload,
  PendenciaAetDnitPayload,
  PendenciasRenovacaoPayload,
  RenovacaoLicencaPreviewPayload,
  StatusRenovacaoLicencaPayload
};
